import { useState } from "react";

import {
  DataTable,
  Input,
  Pagination,
  Stack,
  Toolbar,
  type DataTableColumn,
} from "@/components/ui";

export interface PagedTableProps<Row> {
  readonly rows: readonly Row[];
  readonly columns: readonly DataTableColumn<Row>[];
  readonly caption: string;
  readonly emptyHeadline: string;
  readonly placeholder: string;
  readonly match: (row: Row, needle: string) => boolean;
  readonly pageSize?: number;
  readonly density?: "dense" | "comfortable";
}

/** A filter, one page and a pager, with the table keeping its own empty state at zero rows. */
export function PagedTable<Row>({
  rows,
  columns,
  caption,
  emptyHeadline,
  placeholder,
  match,
  pageSize,
  density,
}: PagedTableProps<Row>) {
  const size = pageSize ?? 10;
  const [query, setQuery] = useState("");
  const [page, setPage] = useState(1);

  const needle = query.trim().toLowerCase();
  const matched = needle === "" ? rows : rows.filter((row) => match(row, needle));
  const pageCount = Math.max(1, Math.ceil(matched.length / size));
  const current = Math.min(page, pageCount);
  const slice = matched.slice((current - 1) * size, current * size);

  return (
    <Stack gap={4}>
      <Toolbar fill>
        <Input
          value={query}
          placeholder={placeholder}
          aria-label={placeholder}
          onChange={(event) => {
            setQuery(event.target.value);
            setPage(1);
          }}
        />
      </Toolbar>
      <DataTable
        caption={caption}
        density={density ?? "dense"}
        rows={slice}
        columns={columns}
        emptyHeadline={needle === "" ? emptyHeadline : "No matches"}
      />
      {matched.length > size ? (
        <Pagination
          page={current}
          pageCount={pageCount}
          pageSize={size}
          total={matched.length}
          label={caption}
          onChange={setPage}
        />
      ) : null}
    </Stack>
  );
}
